import { useEffect, useState } from 'react';
import { adminService } from '@/services/adminService';
import { AdminAction } from '@/types/admin';
import { ShieldCheck, Loader2, AlertCircle } from 'lucide-react';

const actionTypeLabels: Record<string, string> = {
  view_user_details: 'Viewed user profile',
  edit_user_profile: 'Updated user profile',
  grant_admin_role: 'Granted admin role',
  revoke_admin_role: 'Revoked admin role',
  suspend_user: 'Suspended user',
  activate_user: 'Re-activated user',
  export_user_data: 'Exported user data',
  view_analytics: 'Viewed analytics',
  view_all_users: 'Viewed user list'
};

const ANON_PLACEHOLDER = 'Unknown user';

export function AdminAuditLog() {
  const [actions, setActions] = useState<AdminAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadActions();
  }, []);

  const loadActions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getRecentAdminActions();
      setActions(data);
    } catch (err) {
      console.error('Failed to load admin audit log:', err);
      setError('Unable to load admin activity right now.');
    } finally {
      setLoading(false);
    }
  };

  const describeAction = (action: AdminAction) => {
    const base = actionTypeLabels[action.actionType] || action.actionType.replace(/_/g, ' ');
    if (!action.targetUserId) {
      return base;
    }

    return `${base} • ${action.targetUserId.slice(0, 8)}…`;
  };

  const summarizeDetails = (details: Record<string, any> | undefined) => {
    if (!details || Object.keys(details).length === 0) {
      return null;
    }

    if (typeof details.summary === 'string') {
      return details.summary;
    }

    if (typeof details.note === 'string') {
      return details.note;
    }

    if (details.changes && typeof details.changes === 'object') {
      const changedFields = Object.keys(details.changes);
      if (changedFields.length > 0) {
        return `Fields updated: ${changedFields.join(', ')}`;
      }
    }

    try {
      return JSON.stringify(details);
    } catch (error) {
      console.warn('Could not serialize admin action details', error);
      return null;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col h-full">
      <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
        <div className="rounded-full bg-blue-50 p-2">
          <ShieldCheck className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Admin Activity</h2>
          <p className="text-sm text-gray-500">Latest actions from admin users</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mb-3 text-blue-600" />
            Loading recent activity...
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center text-sm text-red-600">
            <AlertCircle className="w-6 h-6 mb-2" />
            {error}
          </div>
        ) : actions.length === 0 ? (
          <div className="py-12 px-6 text-sm text-gray-500 text-center">
            No admin actions recorded yet.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {actions.map((action) => {
              const description = describeAction(action);
              const detail = summarizeDetails(action.actionDetails);
              return (
                <li key={action.id} className="px-6 py-4">
                  <p className="text-sm font-medium text-gray-900">{description}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {action.createdAt.toLocaleString()} • Admin {action.adminUserId?.slice(0, 8) || ANON_PLACEHOLDER}
                  </p>
                  {detail && (
                    <p className="text-xs text-gray-500 mt-2">
                      {detail}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
